import { useMemo, useContext, forwardRef } from 'react';
import styled from '@emotion/styled';
import { QuantAnalysisFilterContext } from '@trinity/chat-components';
import { shouldForwardProp } from '@trinity/chat-components/utils/emotion.js';
import { Filter as FilterIcon, type IconProps } from 'react-feather';

type PresentationProps = {
  filtersCount: number;
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
  color?: string;
};

export const QuantAnalysisFilterPresentation = forwardRef<HTMLButtonElement, PresentationProps>((props, ref) => {
  const isActive = props.filtersCount > 0;
  const hasCustomColor = !!props.color;
  return (
    <Root className={props.className}>
      <Button
        ref={ref}
        onClick={props.onClick}
        aria-label="Filter"
        type="button"
        className={isActive ? 'active' : ''}
        disabled={props.disabled}
        color={props.color}
        $isActive={isActive}
        $hasCustomColor={hasCustomColor}>
        <IconContainer>
          <StyledFilterIcon
            active={false}
            color={props.color}
            $isActive={isActive}
            $hasCustomColor={hasCustomColor} />
          <FilterCircle
            active={props.filtersCount > 0}
            color={props.color}
            $isActive={isActive}
            $hasCustomColor={hasCustomColor} />
          <FilterDivider
            $isActive={isActive}
            $hasCustomColor={hasCustomColor} />
          <span style={{ marginLeft: 5, fontWeight: 500 }}>
            Filter{props.filtersCount > 0 ? ` (${props.filtersCount})` : ''}
          </span>
        </IconContainer>
      </Button>
    </Root>
  );
});

type Props = {
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
  color?: string;
};

export const QuantAnalysisFilterButton = forwardRef<HTMLButtonElement, Props>((props, ref) => {
  const [quantFilter] = useContext(QuantAnalysisFilterContext);
  const filtersCount = useMemo(() => {
    return quantFilter.rowNames.length + quantFilter.columnNames.length;
  }, [quantFilter]);
  return (
    <QuantAnalysisFilterPresentation
      ref={ref}
      filtersCount={filtersCount}
      onClick={props.onClick}
      className={props.className}
      disabled={props.disabled}
      color={props.color} />
  );
});

const Root = styled.div({});

const Button = styled('button', {
  shouldForwardProp: shouldForwardProp('$isActive', '$hasCustomColor'),
})<{ color?: string; $isActive?: boolean; $hasCustomColor?: boolean }>(({ disabled, className, color, $hasCustomColor }) => {
  const isActive = className && className.includes('active');
  const themeColor = color || '#28AAE1';
  const useWhitelabelStyling = $hasCustomColor;

  return {
    display: 'flex',
    alignItems: 'center',
    border: `1px solid ${disabled ? '#f2f2f2' : useWhitelabelStyling ? '#d3d3d3' : themeColor}`,
    borderRadius: 8,
    padding: '6px 8px',
    color: disabled ? '#d3d3d3' : useWhitelabelStyling ? '#666666' : themeColor,
    fontFamily: 'inherit',
    fontWeight: 500,
    fontSize: '1rem',
    marginRight: '10px',
    background: disabled ? '#f2f2f2' : useWhitelabelStyling ? (isActive ? 'var(--gray-l)' : 'var(--gray-xl)') : isActive ? '#E5F5FC' : undefined,
    boxSizing: 'border-box',
    cursor: disabled ? 'default' : 'pointer',
    transition: 'background 0.15s, color 0.15s, border-color 0.15s',
    '&:hover, &.open': {
      background: '#f2f2f2',
      borderColor: '#f2f2f2',
    },
    '&.active': {
      background: useWhitelabelStyling ? 'var(--gray-l)' : '#E5F5FC',
      borderColor: useWhitelabelStyling ? '#d3d3d3' : themeColor,
      color: useWhitelabelStyling ? '#666666' : themeColor,
    },
  };
});

const IconContainer = styled.span({
  position: 'relative',
  height: 20,
  display: 'flex',
  alignItems: 'center',
});

type FilterIconProps = {
  active?: boolean;
  color?: string;
  $isActive?: boolean;
  $hasCustomColor?: boolean;
};

const SizedFilterIcon = (props: IconProps) => (
  <FilterIcon
    {...props}
    size="18px"
    viewBox="0 0 24 24" />
);

const StyledFilterIcon = styled(SizedFilterIcon, {
  shouldForwardProp: prop => !['active', '$isActive', '$hasCustomColor'].includes(prop),
})<FilterIconProps>(({ color, $hasCustomColor }) => {
  const useWhitelabelStyling = $hasCustomColor;
  const iconColor = useWhitelabelStyling ? '#666666' : color || '#323232';
  return {
    color: iconColor,
    stroke: iconColor,
    fill: 'none',
    '& svg': {
      stroke: iconColor,
      fill: 'none',
    },
  };
});

type FilterCircleProps = {
  active?: boolean;
  color?: string;
  $isActive?: boolean;
  $hasCustomColor?: boolean;
};

const FilterCircle = styled('div', {
  shouldForwardProp: prop => !['active', '$isActive', '$hasCustomColor'].includes(prop),
}) <FilterCircleProps>`
  position: absolute;
  height: 6px;
  width: 6px;
  border-radius: 8px;
  background-color: ${props => {
    const useWhitelabelStyling = props.$hasCustomColor;
    return useWhitelabelStyling ? (props.color || '#28aae1') : (props.color || '#28aae1');
  }};
  border: 2px solid ${props => {
    const useWhitelabelStyling = props.$hasCustomColor;
    return useWhitelabelStyling ? (props.active ? 'var(--gray-l)' : 'var(--gray-xl)') : '#E5F5FC';
  }};
  margin-left: 8px;
  margin-bottom: 9px;
  display: ${props => props.active ? 'inline' : 'none'};
`;

const FilterDivider = styled('div', {
  shouldForwardProp: shouldForwardProp('$isActive', '$hasCustomColor'),
})<{ $isActive?: boolean; $hasCustomColor?: boolean }>(({ $hasCustomColor }) => {
  const useWhitelabelStyling = $hasCustomColor;
  return {
    width: '1px',
    height: '16px',
    backgroundColor: useWhitelabelStyling ? 'var(--gray-m);' : 'var(--gray-m);',
    marginLeft: '6px',
    marginRight: '6px',
  };
});
